import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { ShareDetailsForm } from './ShareDetailsForm';
import config from "../config/config";
import { FiUser, FiPhone, FiMapPin, FiArrowRight, FiLoader } from "react-icons/fi"; // Icons add kiye hain

const formSchema = z.object({
  customerName: z.string().min(2, { message: "Full Name is required" }),
  phone: z.string().regex(/^((\+92)|(0092))-{0,1}\d{3}-{0,1}\d{7}$|^\d{11}$|^\d{4}-\d{7}$/, { message: "Valid Pakistani phone number required" }),
  address: z.string().min(5, { message: "Please provide a more detailed address" }),
});

export function QurbaniForm() {
  const [showShareDetails, setShowShareDetails] = useState(false);
  const [registrationData, setRegistrationData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: { customerName: "", phone: "", address: "" },
  });

  useEffect(() => {
    form.setValue("customerName", localStorage.getItem('customerName') || "");
    form.setValue("phone", localStorage.getItem('phone') || "");
  }, [form]);

  const handleSharesSubmit = async (values) => {
    try {
      setIsLoading(true);
      const initialData = {
        shares: 0,
        customer_name: values.customerName,
        phone: values.phone,
        address: values.address,
        email: localStorage.getItem('userEmail')
      };

      const response = await fetch(`${config.baseUrl}/api/qurbani/initial-registration`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(initialData)
      });

      if (!response.ok) throw new Error('Registration failed');

      const result = await response.json();
      setRegistrationData(result);
      setShowShareDetails(true);
    } catch (error) {
      alert('Failed to save registration. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (showShareDetails) {
    return <ShareDetailsForm shares={0} registrationData={registrationData} />;
  }

  // --- Design Helper Classes ---
  const inputWrapper = "relative group";
  const inputIcon = "absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-500 transition-colors";
  const inputBase = "block w-full pl-12 pr-4 py-4 border-2 rounded-2xl bg-white text-slate-900 placeholder-slate-400 outline-none transition-all duration-200";
  const labelBase = "block text-[11px] font-black text-slate-500 uppercase tracking-widest mb-2 ml-1";

  return (
    <div className="min-h-screen  max-w flex items-center justify-center p-4 md:p-8 font-sans">
      {/* Decorative background elements */}

      <div className="max-w-2xl w-full bg-white rounded-[2.5rem] shadow-[0_20px_50px_rgba(0,0,0,0.05)] border border-slate-100 overflow-hidden">

        {/* Top Accent Header */}
        <div className="bg-indigo-800 px-8 py-6 text-center relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500 rounded-full -mr-16 -mt-16 blur-3xl"></div>
          <h1 className="text-2xl font-black text-white tracking-tight uppercase leading-none">
            Customer <span className="text-indigo-500">Registration</span>
          </h1>
        </div>

        <div className="p-8 md:p-12">
          <form onSubmit={form.handleSubmit(handleSharesSubmit)} className="space-y-8">

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">

              {/* Customer Name */}
              <div className="md:col-span-1 space-y-1">
                <label className={labelBase}>Full Name</label>
                <div className={inputWrapper}>
                  <FiUser className={inputIcon} size={18} />
                  <input
                    {...form.register("customerName")}
                    className={`${inputBase} ${form.formState.errors.customerName ? 'border-red-100 focus:border-red-400' : 'border-slate-100 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/5'}`}
                    placeholder="Enter Full Name"
                  />
                </div>
                {form.formState.errors.customerName && (
                  <p className="text-[10px] text-red-500 font-bold mt-1 uppercase ml-2">{form.formState.errors.customerName.message}</p>
                )}
              </div>

              {/* Phone Number */}
              <div className="md:col-span-1 space-y-1">
                <label className={labelBase}>Contact Number</label>
                <div className={inputWrapper}>
                  <FiPhone className={inputIcon} size={18} />
                  <input
                    {...form.register("phone")}
                    className={`${inputBase} ${form.formState.errors.phone ? 'border-red-100 focus:border-red-400' : 'border-slate-100 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/5'}`}
                    placeholder="03xx-xxxxxxx"
                  />
                </div>
                {form.formState.errors.phone && (
                  <p className="text-[10px] text-red-500 font-bold mt-1 uppercase ml-2">{form.formState.errors.phone.message}</p>
                )}
              </div>

              {/* Address */}
              <div className="md:col-span-2 space-y-1">
                <label className={labelBase}>Current Address</label>
                <div className="relative group">
                  <FiMapPin className="absolute left-4 top-5 text-slate-400 group-focus-within:text-blue-500 transition-colors" size={18} />
                  <textarea
                    {...form.register("address")}
                    rows="3"
                    className="block w-full pl-12 pr-4 py-4 border-2 rounded-2xl bg-white text-slate-900 placeholder-slate-400 outline-none transition-all duration-200 border-slate-100 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/5 resize-none"
                    placeholder="Complete street address, city, and area"
                  />
                </div>
                {form.formState.errors.address && (
                  <p className="text-[10px] text-red-500 font-bold mt-1 uppercase ml-2">{form.formState.errors.address.message}</p>
                )}
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="group relative w-full flex justify-center items-center py-3 px-6 border border-transparent text-sm font-black rounded-md text-white bg-indigo-800 hover:bg-indigo-700 transition-all duration-300 transform active:scale-[0.98] disabled:opacity-70 shadow-[0_10px_25px_rgba(37,99,235,0.2)] overflow-hidden"
            >
              <div className="relative z-10 flex items-center gap-3 tracking-[0.2em]">
                {isLoading ? (
                  <FiLoader className="animate-spin" size={20} />
                ) : (
                  <>
                    PROCEED TO DETAILS
                    <FiArrowRight className="group-hover:translate-x-1 transition-transform" size={18} />
                  </>
                )}
              </div>
              {/* Button Shine Effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
            </button>
          </form>

          {/* Footer Info */}
          <div className="mt-6 pt-2 border-t border-slate-200">
            <div className="flex items-center justify-between opacity-90">
              <span className="text-[9px] font-black uppercase tracking-[0.3em] text-indigo-900">Step 1 of 2</span>
              <div className="flex gap-1">
                <div className="w-8 h-1 bg-indigo-800 rounded-full"></div>
                <div className="w-8 h-1 bg-slate-200 rounded-full"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}